/**
 * The log pane. **This** pane — M2-06 extends it into the combat log rather
 * than building a second one, and M1-06's wound messages arrive as more event
 * types through the same door.
 *
 * The design spec calls text content rather than chrome (§3.2), and that has
 * three consequences this file is built around:
 *
 * - **It is DOM, above the canvas, never inside it.** Text drawn into a glyph
 *   grid is not selectable, not scalable and not accessible; a player who
 *   wants to copy what somebody said should be able to.
 * - **It never steals focus.** A line arriving while you are typing must not
 *   move the caret, scroll you away from what you were reading, or take the
 *   keyboard. Everything below follows from that: the pane only auto-scrolls
 *   when it was already at the bottom, and it never calls `focus()`.
 * - **Classes, not colours, in the markup.** Speech, system notices and world
 *   events read differently at a glance, but *what* they read like belongs to
 *   the stylesheet.
 *
 * The buffer is split from the DOM (`LogBuffer` below) so the interesting
 * rules — the scrollback cap, the classification, the timestamp — are testable
 * without a browser.
 */

import { formatGameClock, gameTime, type LogEvent, type LogEventType } from '@annwn/protocol';

/**
 * How a line reads. Deliberately coarse: a handful of classes a player can
 * learn in one sitting, not one colour per event type.
 *
 * M2-06 splits combat off from `world`, and then splits it again by *who it
 * happened to*, because that is the question a player asks of a fight forty
 * times a minute: was that me? The three combat classes are the answer, and
 * they are decided per line against the viewer rather than per event type.
 */
export type LogClass = 'speech' | 'system' | 'world' | 'mine' | 'against-me' | 'nearby';

const CLASS_OF: { readonly [K in LogEventType]: LogClass } = {
  said: 'speech',
  joined: 'system',
  left: 'system',
  examined: 'world',
  moved: 'world',
  blocked: 'world',

  // Combat's own vocabulary. `classFor` below re-reads these against the viewer
  // and promotes them to `mine` / `against-me` / `nearby`; this is the fallback
  // for a line with no viewer, which is what the tests and the archive see.
  attacked: 'nearby',
  guarded: 'nearby',
  blow: 'nearby',
  status: 'nearby',
  died: 'system',

  // M2-07's verbs against a helpless body. `nearby` for the same reason the
  // combat types are: `classFor` promotes it to `mine` or `against-me` once it
  // knows who is reading, and being robbed is emphatically a line you want
  // picked out of the log in the colour that means *you*.
  interacted: 'nearby',

  // M3-07's journal heartbeat. It is not sent to clients at all — it is written
  // straight to storage by `Persistence`, and it carries no prose to render —
  // so this entry exists only because the map is exhaustive over `LogEventType`
  // and that exhaustiveness is worth keeping. `system` is the honest fallback
  // if one ever does arrive down a socket.
  'world-tick': 'system',

  // RC2-01's errand telemetry is normally archive-only, but a physical reward
  // is deliberately sent to its owner with prose. Both read as system notices.
  errand: 'system',

  // RC2-02's "getting better" beat, and unlike `errand` this one is meant to be
  // seen. Its producer is live as of RC2-04: the tally seam in `advance` emits a
  // `practice` line to the crosser when a swing (or a fire, a cut, a cook) lifts
  // them into a new band. `system` is the right reading for a quiet notice about
  // your own progress; RC2-07 may give it a home of its own.
  practice: 'system',
};

/**
 * Event types that are part of a fight, and are filtered as one.
 *
 * `interacted` is deliberately *not* here. The prominence filter's job is to
 * keep an eight-man brawl readable by hiding other people's small change, and
 * somebody kneeling over a body is never small change — a rescue or a robbery
 * two tiles away is exactly the thing you want to be told about.
 */
const COMBAT_TYPES: ReadonlySet<string> = new Set(['attacked', 'guarded', 'blow', 'status']);

export function classOf(type: LogEventType): LogClass {
  return CLASS_OF[type] ?? 'world';
}

/**
 * A line's class *for this reader*.
 *
 * `actors[0]` is the actor a combat event is about — the attacker of a blow,
 * the subject of a status — and `actors[1]` is who it landed on. That ordering
 * is the sim's, held to deliberately in `combat.ts`, and it is what lets the
 * log answer "was that me" without the server sending a per-viewer copy.
 */
export function classFor(event: LogEvent, viewerId?: string): LogClass {
  const base = classOf(event.type);
  if (viewerId === undefined || !COMBAT_TYPES.has(event.type)) return base;

  const [subject, object] = event.actors;
  if (object === viewerId) return 'against-me';
  if (subject === viewerId) return 'mine';
  return 'nearby';
}

/**
 * How loud a line is, for the prominence filter (M1-06).
 *
 * Read off the event's own refs rather than re-derived: a `blow` carries the
 * wound level it caused at `refs[3]`, which is exactly the tuple-beside-the-
 * prose the sim emits so that questions like this stay answerable when the
 * templates have been rewritten twice.
 */
export function prominenceOf(event: LogEvent): 'minor' | 'notable' | 'major' {
  if (event.type === 'died') return 'major';
  // The herders shouting (M5-02). As loud as a death, because by the time
  // anybody has called it the wolves are already in the fold — a raid alarm
  // that scrolled past with the small talk would be the interface losing the
  // village its animals.
  if (event.refs[0] === 'villager' && event.refs[1] === 'alarm') return 'major';
  if (event.type === 'blow') {
    const level = Number(event.refs[3] ?? 0);
    if (level >= 3) return 'major';
    return level > 0 ? 'notable' : 'minor';
  }
  if (event.type === 'attacked' || event.type === 'guarded') return 'minor';
  return 'notable';
}

export interface LogLine {
  /** Monotonic, so a renderer can tell a re-render from a new arrival. */
  readonly id: number;

  /** Game time, `HH:MM`. Wall-clock time would be the player's, not the world's. */
  readonly stamp: string;

  readonly tick: number;
  readonly kind: LogClass;
  readonly text: string;

  /** How loud it is. The live pane hides `minor` unless it touches the viewer. */
  readonly prominence: 'minor' | 'notable' | 'major';

  /** Who it is about, so hovering a line can highlight them on the grid. */
  readonly actors: readonly string[];

  /** Part of a fight, and therefore part of the battle view. */
  readonly combat: boolean;
}

/**
 * Lines kept in scrollback.
 *
 * M0-06 asks for ≥500 without jank. Held at a thousand: the array is the
 * cheap part, and the DOM below trims to match, so the cost is bounded by
 * what a browser will happily lay out rather than by what a session generates.
 * The archive is the server's business (M0-07) — this is a window onto it.
 */
export const SCROLLBACK_LIMIT = 1000;

/** Ticks of quiet that end a fight, for the battle view's purposes. Ten seconds. */
export const BATTLE_GAP_TICKS = 100;

/**
 * Below this, a line is hidden from the live pane unless it touches the viewer.
 *
 * AoD §12.2's rule, and the thing that makes an eight-man brawl readable: the
 * full chain is *always* kept, and `L` shows it. What scrolls past live is what
 * a person in the fight would actually have registered.
 */
export const LIVE_FLOOR = 'notable' as const;

/**
 * Render an event to prose.
 *
 * The server sends `text` for MVP (AoD §12.1: templates live with the sim and
 * the content), so this is normally a passthrough. The fallback matters
 * anyway: an event type added by a newer server than this client still reads
 * as *something* instead of vanishing, and when localisation arrives the
 * client renders from the tuple with `text` as the fallback rather than the
 * other way round.
 */
export function renderEvent(event: LogEvent): string {
  if (event.text !== undefined && event.text.length > 0) return event.text;
  const subject = event.actors.join(', ');
  return subject.length > 0 ? `${subject}: ${event.type}` : event.type;
}

/**
 * A tick the calendar will accept.
 *
 * The archive is long-lived and append-only, and a row written by an older
 * build — or a message from a newer server — can carry anything at all. A log
 * pane that throws on one bad tick takes the whole client's render loop with
 * it, which is a spectacular price for a wrong timestamp.
 */
function safeTick(tick: number): number {
  if (!Number.isFinite(tick)) return 0;
  return Math.max(0, Math.floor(tick));
}

/** The scrollback, as data. No DOM, so the rules above are testable. */
export class LogBuffer {
  private readonly lines: LogLine[] = [];
  private nextId = 1;
  private readonly limit: number;

  /**
   * Whose log this is. Set once the server says who the viewer is, and the
   * reason a line can read as *mine* rather than merely as combat.
   */
  viewerId: string | undefined;

  constructor(limit: number = SCROLLBACK_LIMIT) {
    this.limit = Math.max(1, limit);
  }

  get length(): number {
    return this.lines.length;
  }

  /** Oldest first — reading order. */
  get all(): readonly LogLine[] {
    return this.lines;
  }

  /**
   * The current or last fight, unabridged — what `L` opens (M2-06).
   *
   * A "fight" is delimited by a gap in the combat lines rather than by a battle
   * id, because the sim does not have one and inventing a client-side notion of
   * when a fight started would be a second, disagreeing answer. Ten seconds of
   * nobody swinging is the end of it, which is also how it reads to a player.
   */
  battle(gapTicks = BATTLE_GAP_TICKS): readonly LogLine[] {
    const combat = this.lines.filter((line) => line.combat);
    if (combat.length === 0) return [];

    let start = combat.length - 1;
    while (start > 0) {
      const previous = combat[start - 1] as LogLine;
      const current = combat[start] as LogLine;
      if (current.tick - previous.tick > gapTicks) break;
      start -= 1;
    }

    const from = (combat[start] as LogLine).tick;
    // Every line inside the window, not only the combat ones: what somebody
    // shouted mid-fight is part of the fight.
    return this.lines.filter((line) => line.tick >= from);
  }

  /**
   * Fold events in, returning only the lines that are new.
   *
   * Returning the delta rather than the whole buffer is what keeps the DOM
   * append-only: re-rendering a thousand lines because one arrived is the
   * "jank" the acceptance criterion is about.
   */
  append(events: readonly LogEvent[]): LogLine[] {
    const added: LogLine[] = [];

    for (const event of events) {
      const line: LogLine = {
        id: this.nextId,
        stamp: formatGameClock(gameTime(safeTick(event.tick))),
        tick: event.tick,
        kind: classFor(event, this.viewerId),
        text: renderEvent(event),
        prominence: prominenceOf(event),
        actors: event.actors,
        combat: COMBAT_TYPES.has(event.type) || event.type === 'died',
      };
      this.nextId += 1;
      this.lines.push(line);
      added.push(line);
    }

    // Trim once, after the batch, rather than per line.
    const excess = this.lines.length - this.limit;
    if (excess > 0) this.lines.splice(0, excess);

    return added;
  }

  /** A local note that never went near the server — a hint, an error. */
  note(text: string, tick: number, kind: LogClass = 'system'): LogLine {
    const line: LogLine = {
      id: this.nextId,
      stamp: formatGameClock(gameTime(safeTick(tick))),
      tick,
      kind,
      text,
      prominence: 'notable',
      actors: [],
      combat: false,
    };
    this.nextId += 1;
    this.lines.push(line);
    if (this.lines.length > this.limit) this.lines.splice(0, this.lines.length - this.limit);
    return line;
  }
}

/**
 * How close to the bottom still counts as "following the log", in pixels.
 * A couple of lines of slack, so a scroll nudged by a trackpad does not
 * silently stop the pane from following.
 */
const PINNED_SLACK = 24;

/**
 * Is this line loud enough for the live pane?
 *
 * Anything touching the viewer always shows, however small: a scratch on you
 * matters more than a killing blow across the clearing, and a filter that did
 * not know that would hide the one line a player needed. Everything hidden is
 * still in the buffer — this is a view, and the battle view is the same lines
 * with the filter off.
 */
export function audible(line: LogLine): boolean {
  if (!line.combat) return true;
  if (line.kind === 'mine' || line.kind === 'against-me') return true;
  return line.prominence !== 'minor';
}

export interface LogPaneOptions {
  /**
   * The pointer is over a line about these actors, or has left one. The grid
   * highlights them — the DOM-overlay-to-renderer hook M2-06 asks for.
   */
  onHighlight?(actors: readonly string[]): void;
}

/** The DOM half: append lines, trim, and scroll only when it is welcome. */
export class LogPane {
  private readonly list: HTMLElement;
  private readonly buffer: LogBuffer;
  private readonly options: LogPaneOptions;

  constructor(
    list: HTMLElement,
    buffer: LogBuffer = new LogBuffer(),
    options: LogPaneOptions = {},
  ) {
    this.list = list;
    this.buffer = buffer;
    this.options = options;
  }

  get lines(): readonly LogLine[] {
    return this.buffer.all;
  }

  /** Tell the buffer who is reading, so combat lines can read as *yours*. */
  setViewer(actorId: string): void {
    this.buffer.viewerId = actorId;
  }

  /** The current or last fight, unabridged — for the battle view. */
  get battle(): readonly LogLine[] {
    return this.buffer.battle();
  }

  /**
   * Is the pane following new lines?
   *
   * A player who has scrolled up is reading something. Yanking them back to
   * the bottom because a line arrived loses their place — which is the same
   * category of rudeness as stealing focus, and just as easy to avoid.
   */
  private get pinned(): boolean {
    const distance = this.list.scrollHeight - this.list.scrollTop - this.list.clientHeight;
    return distance <= PINNED_SLACK;
  }

  append(events: readonly LogEvent[]): void {
    this.draw(this.buffer.append(events));
  }

  note(text: string, tick = 0, kind: LogClass = 'system'): void {
    this.draw([this.buffer.note(text, tick, kind)]);
  }

  private draw(added: readonly LogLine[]): void {
    if (added.length === 0) return;

    const follow = this.pinned;
    const shown = added.filter(audible);

    // Everything filtered out is still in the buffer — this is a view, and the
    // battle view is the same lines with the filter off.
    if (shown.length === 0) {
      if (follow) this.list.scrollTop = this.list.scrollHeight;
      return;
    }

    // One fragment for the batch: a catch-up burst after a stall can be a
    // hundred lines, and a hundred separate appends is a hundred reflows.
    const fragment = this.list.ownerDocument.createDocumentFragment();
    for (const line of shown) fragment.append(this.element(line));
    this.list.append(fragment);

    while (this.list.childElementCount > this.buffer.length) {
      this.list.firstElementChild?.remove();
    }

    // Never `focus()`, and never `scrollIntoView()` — both can move the page
    // and take the keyboard away from the chat line.
    if (follow) this.list.scrollTop = this.list.scrollHeight;
  }

  private element(line: LogLine): HTMLElement {
    const document = this.list.ownerDocument;

    const item = document.createElement('li');
    item.className = `log-line log-${line.kind}`;
    item.dataset['tick'] = String(line.tick);

    // Hovering a line lights up whoever it is about, on the grid. Only worth
    // wiring for lines that name somebody — most world lines do not.
    if (line.actors.length > 0 && this.options.onHighlight) {
      const highlight = this.options.onHighlight;
      item.addEventListener('pointerenter', () => {
        highlight(line.actors);
      });
      item.addEventListener('pointerleave', () => {
        highlight([]);
      });
    }

    const stamp = document.createElement('span');
    stamp.className = 'log-stamp';
    stamp.textContent = line.stamp;

    const body = document.createElement('span');
    body.className = 'log-text';
    // `textContent`, never `innerHTML`: the text half of this is player input
    // that has come back off the wire, and the server's sanitizer should not
    // be the only thing standing between a chat line and the DOM.
    body.textContent = line.text;

    item.append(stamp, body);
    return item;
  }
}
